import { useCallback, useState, useEffect } from 'react';
import { AgxColumn, AgxRadioGroup, AgxRow, AgxTextInput } from '@urbanx/agx.ui-components2';
import { ValueType } from '@urbanx/agx.campaigns.client';
import { parseNumber } from 'utils/parseNumber';

export enum NswDepositOption {
  TenPercent = '10%',
  FivePercent = '5%',
  CustomAmount = 'CustomAmount',
}

export enum NswDepositPayableOption {
  QuarterPercentUpfront = '0.25%',
  FullDepositUpfront = 'FullDepositUpfront',
}

export interface NswDepositSelection {
  depositOption: NswDepositOption;
  depositPayableOption: NswDepositPayableOption;
  percent?: number;
  amount?: number;
  depositValueType: ValueType;
}

interface NswDepositOptionsProps {
  id: string;
  onSelectionChange: (selection: NswDepositSelection) => void;
}

const depositAmountOptions = [
  { value: NswDepositOption.TenPercent, label: '10%' },
  { value: NswDepositOption.FivePercent, label: '5%' },
  { value: NswDepositOption.CustomAmount, label: '$__' },
];

const depositPayableOptions = [
  {
    value: NswDepositPayableOption.QuarterPercentUpfront,
    label: '0.25% paid upfront',
  },
  {
    value: NswDepositPayableOption.FullDepositUpfront,
    label: 'Full deposit paid upfront',
  },
];

export function NswDepositOptionsComponent({ id, onSelectionChange }: NswDepositOptionsProps) {
  const [depositOption, setDepositOption] = useState<NswDepositOption>(NswDepositOption.TenPercent);
  const [depositPayableOption, setDepositPayableOption] = useState<NswDepositPayableOption>(
    NswDepositPayableOption.QuarterPercentUpfront
  );
  const [customAmount, setCustomAmount] = useState<number | undefined>(undefined);

  useEffect(() => {
    let percent: number | undefined;
    let amount: number | undefined;
    let depositValueType: ValueType;

    if (depositOption === NswDepositOption.TenPercent) {
      percent = 10;
      depositValueType = ValueType.Percentage;
    } else if (depositOption === NswDepositOption.FivePercent) {
      percent = 5;
      depositValueType = ValueType.Percentage;
    } else {
      amount = customAmount;
      depositValueType = ValueType.Dollar;
    }

    onSelectionChange({
      depositOption,
      depositPayableOption,
      percent,
      amount,
      depositValueType,
    });
  }, [depositOption, depositPayableOption, customAmount]);

  const handleDepositOptionChange = useCallback((value: string) => {
    setDepositOption(value as NswDepositOption);
  }, []);

  const handleDepositPayableOptionChange = useCallback((value: string) => {
    setDepositPayableOption(value as NswDepositPayableOption);
  }, []);

  const handleCustomAmountChange = useCallback((value: string | undefined) => {
    const amount = value ? parseNumber(value) : undefined;
    setCustomAmount(amount);
  }, []);

  return (
    <AgxColumn gap="spacing-lg" fill>
      <AgxColumn gap="spacing-md" fill>
        <AgxRadioGroup
          id={`${id}-deposit-option`}
          label="Deposit"
          layout="stacked"
          options={depositAmountOptions}
          value={depositOption}
          onChange={handleDepositOptionChange}
        />
        {depositOption === NswDepositOption.CustomAmount && (
          <AgxRow gap="spacing-md" fill>
            <AgxTextInput
              id={`${id}-custom-amount`}
              type="currency"
              stretch
              hideOptionalText
              label="Custom Amount"
              defaultValue={customAmount?.toString()}
              onChange={handleCustomAmountChange}
            />
          </AgxRow>
        )}
      </AgxColumn>

      <AgxRadioGroup
        id={`${id}-deposit-payable-option`}
        label="Deposit Payable"
        layout="stacked"
        options={depositPayableOptions}
        value={depositPayableOption}
        onChange={handleDepositPayableOptionChange}
      />
    </AgxColumn>
  );
}
